import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData, MonitorField } from '../../contexts/HedgeFundDataContext';
import { useTableSort } from '../../../../hooks/useTableSort';
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader';

// Types

interface RiskIndicator {
  RiskIndicatorID: number;
  RiskIndicatorName: string;
  RiskIndicatorColor?: string;
}

interface FieldFormData {
  FieldName: string;
  RiskIndicatorID: number;
  AggregationFieldID?: number;
}

const FieldManagement: React.FC = () => {
  const { getAccessToken } = useAuth();
  const { monitorFields, riskIndicatorTypes, loading, refreshMonitorFields, addMonitorField, updateMonitorField, removeMonitorField } = useHedgeFundData();
  
  const [localLoading, setLocalLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Data state
  const [riskIndicators, setRiskIndicators] = useState<RiskIndicator[]>([]);
  
  // Filter state
  const [selectedRiskIndicator, setSelectedRiskIndicator] = useState<number | ''>('');
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<MonitorField | null>(null);
  const [formData, setFormData] = useState<FieldFormData>({
    FieldName: '',
    RiskIndicatorID: 0,
    AggregationFieldID: undefined
  });

  // Update local state when context data loads
  useEffect(() => {
    if (riskIndicatorTypes && riskIndicatorTypes.length > 0) {
      setRiskIndicators(riskIndicatorTypes);
      console.log(`Risk indicator types loaded from context: ${riskIndicatorTypes.length} types`);
    }
  }, [riskIndicatorTypes]);

  const loadRiskIndicators = async () => {
    try {
      setLocalLoading(true);
      console.log('Loading risk indicator types...');
      
      const token = await getAccessToken();
      if (!token) {
        console.error('No access token available for loading risk indicators');
        setError('No access token available. Please login again.');
        return;
      }

      const apiUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/risk-indicators-types`;
      console.log('Fetching risk indicators from:', apiUrl);

      // Try to load from API endpoint
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Risk indicators API response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('Risk indicators data received:', data);
        
        if (Array.isArray(data)) {
          setRiskIndicators(data);
          console.log(`Successfully loaded ${data.length} risk indicator types`);
          
          if (data.length === 0) {
            setError('No risk indicator types found in database. Please check your database configuration.');
          }
        } else {
          console.error('Risk indicators data is not an array:', data);
          setError('Invalid risk indicator data format received from server.');
          setRiskIndicators([]);
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to load risk indicators - HTTP error:', response.status, errorText);
        setError(`Failed to load risk indicator types: ${response.status} ${response.statusText}. ${errorText || ''}`);
        setRiskIndicators([]);
      }
    } catch (err) {
      console.error('Failed to load risk indicators - Network/Connection error:', err);
      setError(`Failed to load risk indicator types: ${err instanceof Error ? err.message : 'Network error'}. Please check your connection and try again.`);
      setRiskIndicators([]);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleCreateField = () => {
    setEditingField(null);
    setFormData({
      FieldName: '',
      RiskIndicatorID: 0,
      AggregationFieldID: undefined
    });
    setDialogOpen(true);
  };

  const handleEditField = (field: MonitorField) => {
    setEditingField(field);
    setFormData({
      FieldName: field.FieldName,
      RiskIndicatorID: field.RiskIndicatorID,
      AggregationFieldID: field.AggregationFieldID
    });
    setDialogOpen(true);
  };

  const handleDeleteField = async (fieldId: number) => {
    if (!confirm('Are you sure you want to delete this field? This may affect existing thresholds and data.')) {
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields/${fieldId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete field');
      }

      setSuccess('Monitor field deleted successfully');
      refreshMonitorFields();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete field');
    }
  };

  const handleSaveField = async () => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');

      const url = editingField 
        ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields/${editingField.FieldID}`
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields`;

      const method = editingField ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save field');
      }

      setSuccess(editingField ? 'Monitor field updated successfully' : 'Monitor field created successfully');
      setDialogOpen(false);
      refreshMonitorFields();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save field');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingField(null);
  };

  const formatDate = (dateString: string | null) => {
    return dateString ? new Date(dateString).toLocaleDateString() : '-';
  };

  const getRiskIndicatorColor = (riskIndicatorId: number) => {
    const indicator = riskIndicators.find(ri => ri.RiskIndicatorID === riskIndicatorId);
    return indicator?.RiskIndicatorColor || '#000000';
  };

  const getRiskIndicatorName = (field: MonitorField) => {
    // First try to use the name from the field data
    if (field.RiskIndicatorName) {
      return field.RiskIndicatorName;
    }
    // Fallback to looking up in the local risk indicators array
    const indicator = riskIndicators.find(ri => ri.RiskIndicatorID === field.RiskIndicatorID);
    return indicator?.RiskIndicatorName || `Risk Indicator ${field.RiskIndicatorID}`;
  };


  // Filter data first
  const filteredData = selectedRiskIndicator 
    ? monitorFields.filter(field => field.RiskIndicatorID === selectedRiskIndicator)
    : monitorFields;

  // Apply sorting to filtered data
  const { sortedData, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredData,
    initialSort: [{ field: 'FieldName', direction: 'asc' }] // Default sort by field name
  });

  // Paginate sorted data
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 'bold', color: '#1a237e' }}>
        Field Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Filters and Actions Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6">
                Risk Indicator Fields
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCreateField}
                  sx={{ backgroundColor: '#1a237e' }}
                >
                  Add Field
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={refreshMonitorFields}
                  disabled={loading.monitorFields}
                >
                  Refresh
                </Button>
              </Box>
            </Box>
            
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Risk Indicator Type</InputLabel>
                  <Select
                    value={selectedRiskIndicator}
                    onChange={(e) => setSelectedRiskIndicator(e.target.value as number | '')}
                    label="Risk Indicator Type"
                    disabled={localLoading}
                  >
                    <MenuItem value="">All Risk Indicators</MenuItem>
                    {riskIndicators.map(indicator => (
                      <MenuItem key={indicator.RiskIndicatorID} value={indicator.RiskIndicatorID}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              backgroundColor: indicator.RiskIndicatorColor
                            }}
                          />
                          {indicator.RiskIndicatorName}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Loading indicator and retry for risk indicators */}
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {localLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">
                        Loading risk indicator types...
                      </Typography>
                    </Box>
                  )}
                  
                  {!localLoading && riskIndicators.length === 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="warning.main">
                        Risk indicator types are loading from context...
                      </Typography>
                      {riskIndicatorTypes.length === 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={loadRiskIndicators}
                          startIcon={<RefreshIcon />}
                        >
                          Manual Retry
                        </Button>
                      )}
                    </Box>
                  )}
                  
                  {!localLoading && riskIndicators.length > 0 && (
                    <Typography variant="body2" color="success.main">
                      {riskIndicators.length} risk indicator types loaded
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Fields Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6">
                Monitor Fields ({filteredData.length} fields)
              </Typography>
              {loading.monitorFields && <CircularProgress size={24} />}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortableTableHeader
                      field="FieldName"
                      label="Field Name"
                      sortDirection={getSortDirection('FieldName')}
                      sortIndex={getSortIndex('FieldName')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="RiskIndicatorID"
                      label="Risk Indicator"
                      sortDirection={getSortDirection('RiskIndicatorID')}
                      sortIndex={getSortIndex('RiskIndicatorID')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="AggregationFieldName"
                      label="Aggregation Field"
                      sortDirection={getSortDirection('AggregationFieldName')}
                      sortIndex={getSortIndex('AggregationFieldName')}
                      onSort={handleSort}
                    />
                    <SortableTableHeader
                      field="CreatedDate"
                      label="Timestamp"
                      sortDirection={getSortDirection('CreatedDate')}
                      sortIndex={getSortIndex('CreatedDate')}
                      onSort={handleSort}
                    />
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {loading.monitorFields ? 'Loading monitor fields...' : 'No monitor fields found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((field) => (
                      <TableRow key={field.FieldID}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {field.FieldName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: getRiskIndicatorColor(field.RiskIndicatorID)
                              }}
                            />
                            {getRiskIndicatorName(field)}
                          </Box>
                        </TableCell>
                        <TableCell>{field.AggregationFieldName ?? '-'}</TableCell>
                        <TableCell>{formatDate(field.CreatedDate ?? null)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditField(field)}
                              title="Edit Field"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteField(field.FieldID)}
                              title="Delete Field"
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Field Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingField ? 'Edit Monitor Field' : 'Create New Monitor Field'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Field Name"
                value={formData.FieldName}
                onChange={(e) => setFormData(prev => ({ ...prev, FieldName: e.target.value }))}
                helperText="Enter a descriptive name for this risk indicator field"
                required
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth required size="small">
                <InputLabel>Risk Indicator Type</InputLabel>
                <Select
                  value={formData.RiskIndicatorID || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, RiskIndicatorID: e.target.value as number }))}
                  label="Risk Indicator Type"
                >
                  {riskIndicators.map(indicator => (
                    <MenuItem key={indicator.RiskIndicatorID} value={indicator.RiskIndicatorID}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: indicator.RiskIndicatorColor
                          }}
                        />
                        {indicator.RiskIndicatorName}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Aggregation Field (Optional)</InputLabel>
                <Select
                  value={formData.AggregationFieldID || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, AggregationFieldID: (e.target.value as number) || undefined }))}
                  label="Aggregation Field (Optional)"
                >
                  <MenuItem value="">None</MenuItem>
                  {monitorFields
                    .filter(field => field.FieldID !== editingField?.FieldID)
                    .map(field => (
                      <MenuItem key={field.FieldID} value={field.FieldID}>
                        {field.FieldName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Field Configuration Help */}
            <Grid item xs={12}>
              <Card sx={{ mt: 1, backgroundColor: '#f5f5f5' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Typography variant="h6" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SettingsIcon />
                    Field Configuration Guide
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    <strong>Risk Indicator Types:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {riskIndicators.map(indicator => (
                      <Chip
                        key={indicator.RiskIndicatorID}
                        label={indicator.RiskIndicatorName}
                        size="small"
                        sx={{ 
                          backgroundColor: indicator.RiskIndicatorColor + '20',
                          color: indicator.RiskIndicatorColor,
                          border: `1px solid ${indicator.RiskIndicatorColor}`
                        }}
                      />
                    ))}
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Aggregation Methods:</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    • <strong>Average:</strong> Calculate mean value across funds<br/>
                    • <strong>Sum:</strong> Add all values together<br/>
                    • <strong>Maximum:</strong> Take the highest value<br/>
                    • <strong>Minimum:</strong> Take the lowest value<br/>
                    • <strong>Weighted Average:</strong> Calculate weighted mean using portfolio weights
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveField} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={!formData.FieldName || !formData.RiskIndicatorID}
            sx={{ backgroundColor: '#1a237e' }}
          >
            {editingField ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldManagement;
